import { AnimatePresence, motion } from "motion/react";
import type { CSSProperties } from "react";
import { ChevronUp } from "lucide-react";
import { useLenis } from "./SmoothScroll";
import { useScrolledPast } from "../hooks/useScrolledPast";
import { useFooterDockOffset } from "../hooks/useFooterDockOffset";
import { useI18n } from "../i18n/I18nContext";

const SHOW_AFTER = 400;

export default function BackToTop() {
  const { t } = useI18n();
  const lenis = useLenis();
  const visible = useScrolledPast(SHOW_AFTER);
  const footerOffset = useFooterDockOffset(visible);

  const shell = "border-[rgba(254,74,43,.65)] bg-[#17191a] text-[var(--color-brand)] shadow-[0_12px_36px_rgba(0,0,0,.45),0_0_14px_rgba(254,74,43,.16)] hover:border-[var(--color-brand)] hover:bg-[#17191a] hover:text-[var(--color-brand)] hover:shadow-[0_12px_36px_rgba(0,0,0,.45),0_0_18px_rgba(254,74,43,.42)] focus-visible:ring-[var(--color-brand)]";

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          key="back-to-top"
          data-back-to-top="true"
          initial={{ opacity: 0, scale: 0.85, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{ "--footer-dock-offset": `${footerOffset}px` } as CSSProperties}
          className={`large-dock-button group fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border backdrop-blur-md transition-[opacity,transform,background-color,color,border-color,box-shadow] hover:-translate-y-0.5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${shell}`}
          aria-label={t("common", "a11y.backToTop")}
          onClick={() => {
            if (lenis) lenis.scrollTo(0);
            else window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 skew-x-[-18deg] bg-white/16 opacity-0 blur-[1px] transition-all duration-700 group-hover:left-[125%] group-hover:opacity-100" />
          <ChevronUp className="large-dock-button__icon relative h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={2.6} aria-hidden />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
